import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { statusBadgeOutlineClass } from "shared/utils/statusChipColor";
import {
  type PhysicalKeyAuditEncoding,
  type PhysicalKeyAuditNamed,
  type PhysicalKeyAuditRecord,
  useSiteKeyAudit,
} from "./siteKeysApi";
import {
  GUEST_EVENTS,
  PHYSICAL_KEY_EVENTS,
  RELATED_SITE_EVENTS,
  physicalKeyEventColor,
  physicalKeyEventLabel,
} from "./siteKeyLabels";
import { GuestLink } from "./SiteKeyParts";
import {
  KEYS_PAGE_SIZE_OPTIONS,
  useDebouncedFilter,
  useKeysSearchParams,
} from "./useKeysSearchParams";

const ALL_EVENTS = "all";

function fobPath(siteId: string, credential: string) {
  return `/admin/sites/${siteId}/keys/${encodeURIComponent(credential)}`;
}

function EventBadge({ event }: { event: string }) {
  return (
    <Badge
      className={statusBadgeOutlineClass(physicalKeyEventColor(event))}
      title={event}
      variant="outline"
    >
      {physicalKeyEventLabel(event)}
    </Badge>
  );
}

function OldToNew({
  oldValue,
  newValue,
}: {
  oldValue: React.ReactNode;
  newValue: React.ReactNode;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span>{oldValue ?? <span className="text-slate-400">—</span>}</span>
      <span aria-hidden="true" className="text-slate-400">
        →
      </span>
      <span className="sr-only">to</span>
      <span>{newValue ?? <span className="text-slate-400">—</span>}</span>
    </span>
  );
}

function guestNode(value: PhysicalKeyAuditNamed | null) {
  return value?.id ? <GuestLink guestId={value.id} name={value.name} /> : null;
}

function plainNode(value: PhysicalKeyAuditNamed | null) {
  return value?.id || value?.name ? <span>{value.name || value.id}</span> : null;
}

function EncodingSummary({
  encoding,
  fobLabel,
  fobHref,
  fobLinkState,
}: {
  encoding: PhysicalKeyAuditEncoding;
  fobLabel: string;
  /** Link to the fob from the dialog title; omitted on the fob's own page. */
  fobHref?: string;
  /** Carries the way back to this table, like the row's fob link. */
  fobLinkState?: unknown;
}) {
  const [open, setOpen] = React.useState(false);
  const total = encoding.addAccess.length + encoding.removeAccess.length;

  if (total === 0) return <span className="text-slate-500">No encoding</span>;

  if (total <= 2) {
    return (
      <div className="space-y-1 text-xs">
        {encoding.addAccess.map((access, index) => (
          // A NoTour id can repeat within one encoding.
          <div key={`add-${access.noTourId}-${index}`}>
            <div className="text-slate-800">+{access.name}</div>
            <div className="text-slate-500">
              No Tour Id: {access.noTourId} | {new Date(access.validFrom).toLocaleString()} —{" "}
              {new Date(access.validTo).toLocaleString()}
            </div>
          </div>
        ))}
        {encoding.removeAccess.map((access, index) => (
          <div key={`remove-${access.noTourId}-${index}`} className="text-slate-500">
            -{access.name} (No Tour Id: {access.noTourId})
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <Button onClick={() => setOpen(true)} size="sm" type="button" variant="outline">
        View ({total})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Encoding —{" "}
              {fobHref ? (
                <Link
                  className="text-blue-700 hover:underline"
                  onClick={() => setOpen(false)}
                  state={fobLinkState}
                  to={fobHref}
                >
                  Fob {fobLabel}
                </Link>
              ) : (
                `Fob ${fobLabel}`
              )}
            </DialogTitle>
            <DialogDescription>
              Access added to and removed from the fob when it was encoded.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto text-sm max-h-[60vh]">
            {encoding.addAccess.length > 0 && (
              <section>
                <h3 className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                  Add access
                </h3>
                <ul className="mt-2 space-y-2">
                  {encoding.addAccess.map((access, index) => (
                    <li key={`add-${access.noTourId}-${index}`}>
                      <div className="font-medium text-slate-800">{access.name}</div>
                      <div className="text-xs text-slate-500">
                        No Tour Id: {access.noTourId} |{" "}
                        {new Date(access.validFrom).toLocaleString()} —{" "}
                        {new Date(access.validTo).toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {encoding.removeAccess.length > 0 && (
              <section>
                <h3 className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                  Remove access
                </h3>
                <ul className="mt-2 space-y-1">
                  {encoding.removeAccess.map((access, index) => (
                    <li key={`remove-${access.noTourId}-${index}`}>
                      <span className="font-medium text-slate-800">{access.name}</span>{" "}
                      <span className="text-xs text-slate-500">
                        (No Tour Id: {access.noTourId})
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AuditChange({
  audit,
  siteId,
  onFobPage,
  linkState,
}: {
  audit: PhysicalKeyAuditRecord;
  siteId: string;
  onFobPage: boolean;
  linkState: unknown;
}) {
  const { event, oldAssignment, newAssignment } = audit;

  if (event === "Encoded") {
    return audit.encoding ? (
      <EncodingSummary
        encoding={audit.encoding}
        fobHref={onFobPage ? undefined : fobPath(siteId, audit.credential)}
        fobLabel={audit.badgeId || audit.credential}
        fobLinkState={linkState}
      />
    ) : (
      <span className="text-slate-400">—</span>
    );
  }

  if (RELATED_SITE_EVENTS.has(event)) {
    // The API puts the sister site in newAssignment (added, assigned, failed) or oldAssignment
    // (unassigned, removed).
    const site = newAssignment?.id ? newAssignment : oldAssignment;
    return site?.id ? (
      <Link to={`/admin/sites/${encodeURIComponent(site.id)}`}>{site.name || site.id}</Link>
    ) : (
      <span className="text-slate-400">—</span>
    );
  }

  if (GUEST_EVENTS.has(event)) {
    return <OldToNew oldValue={guestNode(oldAssignment)} newValue={guestNode(newAssignment)} />;
  }

  // Key groups (and anything else with a value) stay plain text.
  if (oldAssignment || newAssignment) {
    return <OldToNew oldValue={plainNode(oldAssignment)} newValue={plainNode(newAssignment)} />;
  }

  return <span className="text-slate-400">—</span>;
}

/**
 * The key audit table: the whole site's (the Keys tab's Audit log) or, given a credential, one
 * fob's (the fob detail's Audit sub-tab, which hides the Fob # column). Filters by event type and
 * a search (credential, fob # or actor); filters and paging live in the URL.
 */
export default function SiteKeyAuditTable({
  siteId,
  credential,
}: {
  siteId: string;
  credential?: string;
}) {
  const location = useLocation();
  const onFobPage = !!credential;
  const { page, pageSize, getParam, setPage, setPageSize, setFilters } =
    useKeysSearchParams();
  // Ignore an ?event= value the API wouldn't recognise rather than sending it.
  const eventParam = getParam("event");
  const event = (PHYSICAL_KEY_EVENTS as readonly string[]).includes(eventParam)
    ? eventParam
    : "";
  const search = getParam("q");
  const onCommitSearch = React.useCallback(
    (value: string) => setFilters({ q: value }),
    [setFilters],
  );
  const [searchInput, setSearchInput] = useDebouncedFilter(search, onCommitSearch);

  const { data, isLoading, isError, isFetching, isPlaceholderData } = useSiteKeyAudit(siteId, {
    credential,
    event,
    search,
    page,
    pageSize,
  });
  const records = data?.data ?? [];
  const totalCount = data?.totalCount ?? 0;
  const columnCount = onFobPage ? 4 : 5;
  const linkState = { from: `${location.pathname}${location.search}` };

  return (
    <AdminTable
      aria-label={onFobPage ? "Fob audit" : "Site key audit"}
      aria-busy={isFetching}
      toolbar={
        <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center">
          <Select
            value={event || ALL_EVENTS}
            onValueChange={(value) =>
              setFilters({ event: value === ALL_EVENTS ? null : value })
            }
          >
            <SelectTrigger
              aria-label="Event type"
              className="w-full bg-white h-10! border-slate-200 sm:w-60"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_EVENTS}>All events</SelectItem>
              {PHYSICAL_KEY_EVENTS.map((value) => (
                <SelectItem key={value} value={value}>
                  {physicalKeyEventLabel(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="relative block w-full sm:w-80">
            <span className="sr-only">Search audit</span>
            <Search
              aria-hidden="true"
              className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 size-4 text-slate-400"
            />
            <input
              className="w-full h-10 py-2 pr-3 text-sm transition-colors bg-white border rounded-md outline-none border-slate-200 pl-9 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              placeholder={
                onFobPage ? "Search by actor" : "Search by fob #, credential or actor"
              }
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </label>
        </div>
      }
      footer={
        <AdminTablePagination
          currentPage={page}
          pageSize={pageSize}
          pageSizeOptions={KEYS_PAGE_SIZE_OPTIONS}
          visibleRowCount={records.length}
          // Also while the next page loads (the previous page stays on screen meanwhile).
          isLoading={isLoading || isPlaceholderData}
          onNext={() => setPage(page + 1)}
          onPrevious={() => setPage(Math.max(0, page - 1))}
          onPageSizeChange={setPageSize}
          disablePrevious={page === 0}
          disableNext={totalCount <= (page + 1) * pageSize}
        />
      }
    >
      <thead>
        <tr>
          <th scope="col">Time</th>
          {!onFobPage && <th scope="col">Fob #</th>}
          <th scope="col">Event</th>
          <th scope="col">Actor</th>
          <th scope="col">Change</th>
        </tr>
      </thead>
      <tbody>
        {isError ? (
          <tr>
            <td colSpan={columnCount} className="py-16 text-sm text-center text-rose-700">
              Error loading the audit log.
            </td>
          </tr>
        ) : isLoading ? (
          <tr>
            <td colSpan={columnCount} className="py-16 text-sm text-center text-slate-500">
              Loading audit log...
            </td>
          </tr>
        ) : records.length === 0 ? (
          <tr>
            <td colSpan={columnCount} className="py-16 text-sm text-center text-slate-500">
              {event || search ? "No audit records match these filters." : "No audit records found."}
            </td>
          </tr>
        ) : (
          records.map((audit) => (
            <tr key={audit.id}>
              <td className="whitespace-nowrap">{new Date(audit.timestamp).toLocaleString()}</td>
              {!onFobPage && (
                <td className="whitespace-nowrap">
                  <Link to={fobPath(siteId, audit.credential)} state={linkState}>
                    {audit.badgeId || audit.credential}
                  </Link>
                </td>
              )}
              <td>
                <EventBadge event={audit.event} />
              </td>
              <td>
                <div>{audit.actor?.name || <span className="text-slate-400">—</span>}</div>
                {audit.actor?.auth0UserId && (
                  <div className="text-xs truncate text-slate-500 max-w-48">
                    {audit.actor.auth0UserId}
                  </div>
                )}
              </td>
              <td>
                <AuditChange
                  audit={audit}
                  linkState={linkState}
                  onFobPage={onFobPage}
                  siteId={siteId}
                />
              </td>
            </tr>
          ))
        )}
      </tbody>
    </AdminTable>
  );
}
